import { useEffect, useMemo, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { OrbitControls, OrthographicCamera, PerspectiveCamera } from "@react-three/drei";
import type { OrbitControls as OrbitControlsImpl } from "three-stdlib";
import * as THREE from "three";
import { VIEW } from "@/config/designer";
import { useDesignerStore, type ViewMode } from "@/state/designerStore";

interface CameraRigProps {
  width: number;
  height: number;
  depth: number;
}

const toRadians = (deg: number): number => (deg * Math.PI) / 180;

/**
 * Direction from the block towards the camera for each standard view.
 *
 * The plan view is nudged a quarter of a degree off vertical on purpose. Dead
 * on the pole the orbit frame has no defined rotation about the view axis and
 * the camera can spin unpredictably; a hair of tilt keeps it stable and skews a
 * plan-view cut by well under a millimetre across the tallest blank.
 */
const VIEW_DIRECTIONS: Record<Exclude<ViewMode, "free">, [number, number, number]> = {
  top: [0, 1, 0.0044],
  front: [0, 0, 1],
  back: [0, 0, -1],
  right: [1, 0, 0],
  left: [-1, 0, 0],
};

/**
 * Two cameras, one set of controls.
 *
 * Sculpting happens in perspective, which is what lets you read whether a
 * surface is coming towards you or falling away. The five standard elevations
 * are orthographic, because a parallel projection is the only one where a line
 * drawn on screen sweeps through the block as a straight prismatic cut — the
 * Cut tool depends on it.
 *
 * Whichever camera is active, vertical orbit stops level with the orbit target
 * so the eye can look along the mounting plane but never underneath it, panning
 * is fenced to a box around the block, and the eye is kept above y = 0 as a
 * final backstop. The workpiece itself never moves.
 */
export function CameraRig({ width, height, depth }: CameraRigProps): React.ReactElement {
  const viewMode = useDesignerStore((s) => s.viewMode);
  const viewEpoch = useDesignerStore((s) => s.viewEpoch);
  const navigationMode = useDesignerStore((s) => s.navigationMode);
  const orthographic = viewMode !== "free";

  const controlsRef = useRef<OrbitControlsImpl>(null);
  const perspectiveRef = useRef<THREE.PerspectiveCamera>(null);
  const orthographicRef = useRef<THREE.OrthographicCamera>(null);

  // Swapping cameras makes drei rebuild OrbitControls, and a freshly built
  // controls instance re-derives the camera's orientation from its own target.
  // Depending on both means the view is applied only once that has settled.
  const activeCamera = useThree((state) => state.camera);
  const activeControls = useThree((state) => state.controls) as OrbitControlsImpl | null;

  const size = useThree((state) => state.size);
  const sizeRef = useRef(size);
  sizeRef.current = size;

  const diagonal = Math.hypot(width, depth, height);
  const centre = useMemo(
    () => new THREE.Vector3(width / 2, height / 2, depth / 2),
    [width, height, depth],
  );
  const freeTarget = useMemo(
    () => new THREE.Vector3(width / 2, height * 0.32, depth / 2),
    [width, height, depth],
  );
  const corners = useMemo(() => {
    const list: THREE.Vector3[] = [];
    for (let c = 0; c < 8; c++) {
      list.push(new THREE.Vector3(c & 1 ? width : 0, c & 2 ? height : 0, c & 4 ? depth : 0));
    }
    return list;
  }, [width, height, depth]);

  useEffect(() => {
    const orbit = activeControls ?? controlsRef.current;
    if (!orbit) return;

    if (!orthographic) {
      const camera = perspectiveRef.current;
      // Wait for the projection swap to land before positioning.
      if (!camera || activeCamera !== camera) return;
      camera.position.set(
        width / 2 - diagonal * 0.72,
        height + diagonal * 0.62,
        depth / 2 + diagonal * 0.92,
      );
      camera.near = Math.max(1, diagonal * 0.01);
      camera.far = diagonal * 24;
      camera.updateProjectionMatrix();
      orbit.minZoom = 0;
      orbit.maxZoom = Infinity;
      orbit.target.copy(freeTarget);
      orbit.update();
      return;
    }

    const camera = orthographicRef.current;
    if (!camera || activeCamera !== camera) return;

    const direction = new THREE.Vector3(...VIEW_DIRECTIONS[viewMode]).normalize();
    camera.position
      .copy(centre)
      .addScaledVector(direction, diagonal * VIEW.standardViewDistanceFactor);
    camera.near = 0.1;
    camera.far = diagonal * 6;
    camera.up.set(0, 1, 0);
    camera.lookAt(centre);
    camera.updateMatrixWorld();

    // Zoom to fit the block's silhouette from this angle.
    const right = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 0).normalize();
    const up = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 1).normalize();
    let uMin = Infinity;
    let uMax = -Infinity;
    let vMin = Infinity;
    let vMax = -Infinity;
    for (const corner of corners) {
      const u = corner.dot(right);
      const v = corner.dot(up);
      if (u < uMin) uMin = u;
      if (u > uMax) uMax = u;
      if (v < vMin) vMin = v;
      if (v > vMax) vMax = v;
    }
    const { width: viewWidth, height: viewHeight } = sizeRef.current;
    const zoom = Math.min(
      (viewWidth * VIEW.standardViewFill) / Math.max(uMax - uMin, 1),
      (viewHeight * VIEW.standardViewFill) / Math.max(vMax - vMin, 1),
    );
    camera.zoom = zoom;
    camera.updateProjectionMatrix();

    orbit.minZoom = zoom * VIEW.orthoZoomMin;
    orbit.maxZoom = zoom * VIEW.orthoZoomMax;
    orbit.target.copy(centre);
    orbit.update();
  }, [
    viewMode,
    viewEpoch,
    orthographic,
    activeCamera,
    activeControls,
    width,
    height,
    depth,
    diagonal,
    centre,
    freeTarget,
    corners,
  ]);

  const panLimit = diagonal * VIEW.panLimitFactor;

  useFrame(() => {
    const orbit = controlsRef.current;
    if (!orbit) return;
    const target = orbit.target;
    target.x = clamp(target.x, centre.x - panLimit, centre.x + panLimit);
    target.z = clamp(target.z, centre.z - panLimit, centre.z + panLimit);
    target.y = clamp(target.y, 0, height * 0.9);
    if (orbit.object.position.y < 1) orbit.object.position.y = 1;
  });

  return (
    <>
      <PerspectiveCamera
        ref={perspectiveRef}
        makeDefault={!orthographic}
        fov={38}
        near={Math.max(1, diagonal * 0.01)}
        far={diagonal * 24}
      />
      <OrthographicCamera
        ref={orthographicRef}
        makeDefault={orthographic}
        near={0.1}
        far={diagonal * 6}
      />
      <OrbitControls
        makeDefault
        ref={controlsRef}
        enableDamping
        dampingFactor={0.09}
        rotateSpeed={0.75}
        zoomSpeed={0.85}
        panSpeed={0.7}
        screenSpacePanning={false}
        minPolarAngle={toRadians(VIEW.minPolarAngleDeg)}
        maxPolarAngle={toRadians(VIEW.maxPolarAngleDeg)}
        minDistance={diagonal * VIEW.minDistanceFactor}
        maxDistance={diagonal * VIEW.maxDistanceFactor}
        mouseButtons={{
          LEFT:
            navigationMode === "orbit"
              ? THREE.MOUSE.ROTATE
              : navigationMode === "pan"
                ? THREE.MOUSE.PAN
                : navigationMode === "zoom"
                  ? THREE.MOUSE.DOLLY
                  : (-1 as THREE.MOUSE),
          MIDDLE: THREE.MOUSE.ROTATE,
          RIGHT: -1 as THREE.MOUSE,
        }}
        touches={{
          ONE: navigationMode === "pan" ? THREE.TOUCH.PAN : THREE.TOUCH.ROTATE,
          TWO: THREE.TOUCH.DOLLY_PAN,
        }}
      />
    </>
  );
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
